/**
 * Google Integrations Service
 *
 * API client for Google enterprise integrations (GSC, GA4, Ads, Merchant Center).
 *
 * PRD: docs/delivery/stories/google-oauth-foundation/prd.md
 * GitHub Issue: #378
 */

import { authFetch } from '@/utils/auth-fetch';

// Service types
export type GoogleService = 'gsc' | 'ga4' | 'ads' | 'merchant_center';

export interface ServiceStatus {
  connected: boolean;
  propertyUrl?: string;
  propertyId?: string;
  propertyName?: string;
  accountId?: string;
  customerId?: string;
  accountName?: string;
  managerId?: string;
  merchantId?: string;
  merchantName?: string;
  permissionLevel?: string;
}

export interface GoogleIntegrationStatus {
  connected: boolean;
  provider: 'google';
  status: 'pending' | 'active' | 'expired' | 'revoked' | 'error' | 'disconnected';
  email?: string;
  authMethod: 'oauth' | 'service_account';
  connectedAt?: string;
  tokenExpiresAt?: string;
  services: {
    gsc: ServiceStatus;
    ga4: ServiceStatus;
    ads: ServiceStatus;
    merchant_center: ServiceStatus;
  };
  sync: {
    lastSyncAt?: string;
    lastStatus?: string;
    lastError?: string;
    rowsProcessed?: number;
    consecutiveFailures?: number;
  };
}

export interface GSCProperty {
  siteUrl: string;
  permissionLevel: string;
}

export interface GA4Property {
  accountId: string;
  accountName: string;
  propertyId: string;
  propertyName: string;
}

export interface AdsAccount {
  customerId: string;
  descriptiveName: string;
  currencyCode: string;
  isManager: boolean;
}

export interface AuditLogEntry {
  id: string;
  action: string;
  details: Record<string, any>;
  ip_address: string;
  created_at: string;
  actor_email?: string;
  actor_name?: string;
}

export interface SyncResult {
  success: boolean;
  status?: 'started' | 'success';
  rowsProcessed?: number;
  message: string;
}

class GoogleIntegrationsService {
  /**
   * Generate OAuth authorization URL
   */
  async getAuthUrl(siteId: string, services: GoogleService[]): Promise<{
    authUrl: string;
    services: GoogleService[];
    expiresIn: number;
  }> {
    const response = await authFetch(
      `/api/integrations/google/auth-url?siteId=${siteId}&services=${services.join(',')}`
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate auth URL');
    }
    return response.json();
  }

  /**
   * Get integration status for a site
   */
  async getStatus(siteId: string): Promise<GoogleIntegrationStatus> {
    const response = await authFetch(`/api/integrations/google/status?siteId=${siteId}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch integration status');
    }
    return response.json();
  }

  /**
   * Disconnect Google account
   */
  async disconnect(siteId: string): Promise<{ success: boolean; message: string }> {
    const response = await authFetch('/api/integrations/google/disconnect', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to disconnect');
    }
    return response.json();
  }

  /**
   * Update integration configuration
   */
  async configure(siteId: string, config: {
    gscPropertyUrl?: string;
    ga4PropertyId?: string;
    ga4PropertyName?: string;
    ga4AccountId?: string;
    adsCustomerId?: string;
    adsAccountName?: string;
    adsManagerId?: string;
    merchantCenterId?: string;
    merchantCenterName?: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await authFetch('/api/integrations/google/configure', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteId, ...config }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update configuration');
    }
    return response.json();
  }

  /**
   * Get available GSC properties
   */
  async getGSCProperties(siteId: string): Promise<{ properties: GSCProperty[]; count: number }> {
    const response = await authFetch(`/api/integrations/google/properties?siteId=${siteId}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch GSC properties');
    }
    return response.json();
  }

  /**
   * Get available GA4 properties
   */
  async getGA4Properties(siteId: string): Promise<{ properties: GA4Property[]; count: number }> {
    const response = await authFetch(`/api/integrations/google/ga4-properties?siteId=${siteId}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch GA4 properties');
    }
    return response.json();
  }

  /**
   * Get available Google Ads accounts
   */
  async getAdsAccounts(siteId: string): Promise<{ accounts: AdsAccount[]; count: number }> {
    const response = await authFetch(`/api/integrations/google/ads-accounts?siteId=${siteId}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch Ads accounts');
    }
    return response.json();
  }

  /**
   * Upload service account key for Merchant Center
   */
  async uploadServiceAccount(siteId: string, serviceAccountKey: string, merchantCenterId: string): Promise<{
    success: boolean;
    message: string;
    merchantCenterId: string;
    email: string;
  }> {
    const response = await authFetch('/api/integrations/google/service-account', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteId, serviceAccountKey, merchantCenterId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to upload service account');
    }
    return response.json();
  }

  /**
   * Get audit log for integrations
   */
  async getAuditLog(siteId: string, limit = 50, offset = 0): Promise<{
    logs: AuditLogEntry[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const response = await authFetch(
      `/api/integrations/audit-log?siteId=${siteId}&limit=${limit}&offset=${offset}`
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch audit log');
    }
    return response.json();
  }

  // =========================================================================
  // Sync Triggers
  // =========================================================================

  /**
   * Trigger GSC sync
   */
  async syncGSC(siteId: string, isBackfill = false): Promise<SyncResult> {
    const response = await authFetch('/api/integrations/google/gsc/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteId, isBackfill }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'GSC sync failed');
    }
    return response.json();
  }

  /**
   * Trigger GA4 sync
   */
  async syncGA4(siteId: string, isBackfill = false): Promise<SyncResult> {
    const response = await authFetch('/api/integrations/google/ga4/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteId, isBackfill }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'GA4 sync failed');
    }
    return response.json();
  }

  /**
   * Trigger Ads sync
   */
  async syncAds(siteId: string, isBackfill = false): Promise<SyncResult> {
    const response = await authFetch('/api/integrations/google/ads/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteId, isBackfill }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ads sync failed');
    }
    return response.json();
  }

  /**
   * Trigger Ads landing page sync
   */
  async syncAdsLandingPages(siteId: string): Promise<SyncResult> {
    const response = await authFetch('/api/integrations/google/ads/landing-pages/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Landing page sync failed');
    }
    return response.json();
  }

  /**
   * Trigger Shopping sync
   */
  async syncShopping(siteId: string, isBackfill = false): Promise<SyncResult> {
    const response = await authFetch('/api/integrations/google/shopping/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteId, isBackfill }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Shopping sync failed');
    }
    return response.json();
  }

  /**
   * Trigger Merchant Center sync
   */
  async syncMerchantCenter(siteId: string): Promise<{
    success: boolean;
    totalProducts: number;
    disapprovedCount: number;
    message: string;
  }> {
    const response = await authFetch('/api/integrations/google/shopping/mc-sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Merchant Center sync failed');
    }
    return response.json();
  }

  // =========================================================================
  // Analytics Provider
  // =========================================================================

  /**
   * Get current analytics provider
   */
  async getAnalyticsProvider(siteId: string): Promise<{
    provider: 'matomo' | 'ga4' | 'pixel';
    availableProviders: string[];
  }> {
    const response = await authFetch(`/api/integrations/analytics/provider?siteId=${siteId}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get analytics provider');
    }
    return response.json();
  }

  /**
   * Set analytics provider
   */
  async setAnalyticsProvider(siteId: string, provider: 'matomo' | 'ga4' | 'pixel'): Promise<{
    success: boolean;
    provider: string;
    message: string;
  }> {
    const response = await authFetch('/api/integrations/analytics/provider', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteId, provider }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to set analytics provider');
    }
    return response.json();
  }
}

export const googleIntegrationsService = new GoogleIntegrationsService();
